import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  CASSEMS_COMBO_ASSENTO_ID,
  CASSEMS_COMBO_FOOT_ID,
  CASSEMS_COMBO_PLATAFORMA_ID,
  CASSEMS_COMBO_PRECO_PAR,
  precoUnitarioCampanhaCassems,
} from '../content/cassemsLandingCopy';

const STORAGE_KEY = 'lunins_cassems_cart_v1';

export type CassemsCartLine = {
  id: string;
  nome: string;
  precoUnitCheio: number;
  precoUnitComDesconto: number;
  quantidade: number;
  imagemArquivo?: string;
};

export type CassemsCartTotals = {
  subtotalCheio: number;
  /** Soma (preço promo unitário × qtd) antes do ajuste do combo R$ 4.000 */
  subtotalPrecosPromo: number;
  valorDescontoCombo: number;
  /** Quantos pares combo (assento + pé ou plataforma) */
  paresCombo: number;
  comboDetalhe: string | null;
  subtotalComDesconto: number;
  /** Legado para PDF/API: 0 = preços fixos, não % único */
  descontoPercentual: number;
};

type CassemsCartContextValue = {
  lines: CassemsCartLine[];
  addItem: (line: Omit<CassemsCartLine, 'quantidade'> & { quantidade?: number }) => void;
  setQty: (id: string, qty: number) => void;
  removeLine: (id: string) => void;
  clear: () => void;
  totals: CassemsCartTotals;
};

const CassemsCartContext = createContext<CassemsCartContextValue | null>(null);

function loadInitialLines(): CassemsCartLine[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as CassemsCartLine[];
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function qtyPorId(lines: CassemsCartLine[], id: string): number {
  return lines.filter((l) => l.id === id).reduce((a, l) => a + l.quantidade, 0);
}

/**
 * Emparelha assentos com plataforma (prioridade: maior economia) e depois com pé.
 * Retorna economia total vs somar promo individual.
 */
function calcularEconomiaCombo(lines: CassemsCartLine[]): {
  valorDescontoCombo: number;
  paresCombo: number;
  comboDetalhe: string | null;
} {
  let nAss = qtyPorId(lines, CASSEMS_COMBO_ASSENTO_ID);
  let nFoot = qtyPorId(lines, CASSEMS_COMBO_FOOT_ID);
  let nPlat = qtyPorId(lines, CASSEMS_COMBO_PLATAFORMA_ID);

  const pAss = precoUnitarioCampanhaCassems(CASSEMS_COMBO_ASSENTO_ID, 4439);
  const pFoot = precoUnitarioCampanhaCassems(CASSEMS_COMBO_FOOT_ID, 2599);
  const pPlat = precoUnitarioCampanhaCassems(CASSEMS_COMBO_PLATAFORMA_ID, 2699);

  const parPlat = pAss + pPlat;
  const parFoot = pAss + pFoot;
  const savingPlat = Math.max(0, parPlat - CASSEMS_COMBO_PRECO_PAR);
  const savingFoot = Math.max(0, parFoot - CASSEMS_COMBO_PRECO_PAR);

  let paresPlat = 0;
  let paresFoot = 0;
  let a = nAss;

  const usePlat = Math.min(nPlat, a);
  paresPlat = usePlat;
  a -= usePlat;

  const useFoot = Math.min(nFoot, a);
  paresFoot = useFoot;

  const valorDescontoCombo = paresPlat * savingPlat + paresFoot * savingFoot;
  const paresCombo = paresPlat + paresFoot;

  if (paresCombo === 0) {
    return { valorDescontoCombo: 0, paresCombo: 0, comboDetalhe: null };
  }

  const parts: string[] = [];
  if (paresPlat > 0) {
    parts.push(`${paresPlat}× assento + plataforma (R$ ${CASSEMS_COMBO_PRECO_PAR.toLocaleString('pt-BR')} o par)`);
  }
  if (paresFoot > 0) {
    parts.push(`${paresFoot}× assento + pés (R$ ${CASSEMS_COMBO_PRECO_PAR.toLocaleString('pt-BR')} o par)`);
  }
  return {
    valorDescontoCombo: Math.round(valorDescontoCombo * 100) / 100,
    paresCombo,
    comboDetalhe: parts.join(' · '),
  };
}

export function CassemsCartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CassemsCartLine[]>(loadInitialLines);

  const addItem = useCallback(
    (line: Omit<CassemsCartLine, 'quantidade'> & { quantidade?: number }) => {
      const q = Math.max(1, Math.min(99, line.quantidade ?? 1));
      setLines((prev) => {
        const idx = prev.findIndex((x) => x.id === line.id);
        let next: CassemsCartLine[];
        if (idx >= 0) {
          next = [...prev];
          const merged = next[idx]!.quantidade + q;
          next[idx] = { ...next[idx]!, quantidade: Math.min(99, merged) };
        } else {
          next = [...prev, { ...line, quantidade: q }];
        }
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    []
  );

  const setQty = useCallback((id: string, qty: number) => {
    const q = Math.max(0, Math.min(99, Math.floor(qty)));
    setLines((prev) => {
      const next = q === 0 ? prev.filter((x) => x.id !== id) : prev.map((x) => (x.id === id ? { ...x, quantidade: q } : x));
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const removeLine = useCallback((id: string) => {
    setLines((prev) => {
      const next = prev.filter((x) => x.id !== id);
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setLines([]);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const totals = useMemo((): CassemsCartTotals => {
    let subtotalCheio = 0;
    let subtotalPrecosPromo = 0;
    for (const l of lines) {
      subtotalCheio += l.precoUnitCheio * l.quantidade;
      subtotalPrecosPromo += l.precoUnitComDesconto * l.quantidade;
    }
    const { valorDescontoCombo, paresCombo, comboDetalhe } = calcularEconomiaCombo(lines);
    const subtotalComDesconto = Math.round((subtotalPrecosPromo - valorDescontoCombo) * 100) / 100;

    return {
      subtotalCheio,
      subtotalPrecosPromo,
      valorDescontoCombo,
      paresCombo,
      comboDetalhe,
      subtotalComDesconto,
      descontoPercentual: 0,
    };
  }, [lines]);

  const value = useMemo(
    () => ({
      lines,
      addItem,
      setQty,
      removeLine,
      clear,
      totals,
    }),
    [lines, addItem, setQty, removeLine, clear, totals]
  );

  return <CassemsCartContext.Provider value={value}>{children}</CassemsCartContext.Provider>;
}

export function useCassemsCart() {
  const ctx = useContext(CassemsCartContext);
  if (!ctx) throw new Error('useCassemsCart fora de CassemsCartProvider');
  return ctx;
}
